import { BTSelectItem, type IBaseEntity } from "types/apiResponse/apiResponse";
import { AccountingIntegrationType } from "types/enum";

import { getIntegrationTypeName } from "commonComponents/financial/Common/Accounting.types";
import {
    BillForVoiding,
    type ICanVoidPayment,
} from "commonComponents/financial/PaymentVoidConfirm/PaymentVoidConfirm";
import { GridEntity } from "commonComponents/utilities/Grid/GridContainer.types";

import type { default as BillListJson } from "./BillList.api.json";

export type BillListFormActions = "newBill" | "checkedAction" | "cancel" | "download" | undefined;

export class BillGridEntity extends GridEntity {
    constructor(data: typeof BillListJson) {
        super(data);
        this.canCreateOwnerInvoice = data.canCreateOwnerInvoice;
        this.canUpdateOwnerInvoice = data.canUpdateOwnerInvoice;
        this.canVoid = data.canVoid;
        this.canMarkReadyForPayment = data.canMarkReadyForPayment;
        this.canPrintLienWaivers = data.canPrintLienWaivers;
        this.accountingIntegrationName = getIntegrationTypeName(
            data.accountingIntegrationType ?? AccountingIntegrationType.None,
        );
        this.accountingIntegrationType = data.accountingIntegrationType;
        this.canUpdateAccountingStatus = data.canUpdateAccountingStatus;
        this.canSendToAccounting = data.canSendToAccounting;
        this.canMarkAsBillableToOwner = data.canMarkAsBillableToOwner;
        this.isBillableToOwnerCheckedPreference = data.isBillableToOwnerCheckedPreference;
        this.invoiceableBillIds = data.invoiceableBillIds;
        this.canPayOnline = data.canPayOnline;
        this.showPayOnlineMarketing = data.showPayOnlineMarketing;
        this.canPayInFull = data.canPayInFull;
        this.showAccountingReauthBanner = data.showAccountingReauthBanner;
        this.accountingReauthBannerMessage = data.accountingReauthBannerMessage;
        this.accountingReauthBannerTitle = data.accountingReauthBannerTitle;
        this.billsToApproveCount = data.billsToApproveCount ?? 0;
        this.hasDraftOnlyAccess = data.hasDraftOnlyAccess ?? false;
    }
    canCreateOwnerInvoice: boolean;
    canUpdateOwnerInvoice: boolean;
    canVoid: boolean;
    canPrintLienWaivers: boolean;
    accountingIntegrationName?: string;
    accountingIntegrationType?: AccountingIntegrationType;
    canUpdateAccountingStatus: boolean;
    canSendToAccounting: boolean;
    canMarkAsBillableToOwner: boolean;
    canPayOnline: boolean;
    showPayOnlineMarketing: boolean;
    canPayInFull: boolean;
    canMarkReadyForPayment: boolean;
    isBillableToOwnerCheckedPreference: boolean;
    invoiceableBillIds: number[];
    showAccountingReauthBanner: boolean;
    accountingReauthBannerMessage?: string;
    accountingReauthBannerTitle?: string;
    billsToApproveCount: number;
    hasDraftOnlyAccess: boolean;
}

export class MassVoidAlertInfo implements ICanVoidPayment {
    constructor(data: any) {
        this.voidTitle = data.voidTitle;
        this.voidMessage = data.voidMessage;
        this.voidCheckboxMessage = data.voidCheckboxMessage;
        this.voidTooltip = data.voidTooltip;
        this.additionalVoidAlert = data.additionalVoidAlert;
        this.paymentIdsToVoid = data.paymentIdsToVoid.map((p: any) => Number(p));
        this.billsBeingVoided = data.billsToVoid?.map((x: any) => new BillForVoiding(x));
        this.billsTiedToSamePayment = data.billsTiedToSamePayment?.map(
            (x: any) => new BillForVoiding(x),
        );
    }
    voidTitle: string;
    voidMessage: string;
    voidCheckboxMessage?: string | undefined;
    voidTooltip?: string | undefined;
    additionalVoidAlert?: string | undefined;
    paymentIdsToVoid: number[];
    billsTiedToSamePayment?: BillForVoiding[];
    billsBeingVoided?: BillForVoiding[];
}

export class VoidSelectedPaymentsResponse {
    constructor(data?: any) {
        this.totalPaymentCount = Number(data.totalPaymentCount);
        this.voidedPaymentCount = Number(data.voidedPaymentCount);
    }
    totalPaymentCount: number;
    voidedPaymentCount: number;
}

export class MassEmailLienWaiversDataResponse {
    constructor(data?: any) {
        this.documentInstanceIds = data.documentInstanceIds.map((d: any) => Number(d));
        this.defaultSubject = data.defaultSubject;
    }
    documentInstanceIds: number[];
    defaultSubject: string;
}

export class MassUpdateAccountingStatusResponse {
    constructor(data: any) {
        this.totalPaymentCount = data.totalPaymentCount;
        this.successfulSyncCount = data.successfulSyncCount;
    }
    totalPaymentCount: number;
    successfulSyncCount: number;
}

export class CanSendEntitiesToAccountingResponse {
    constructor(data: any) {
        this.validIds = data.validAccountingEntityIds.map((i: any) => Number(i));
        this.invalidIds = data.invalidAccountingEntityIds.map((i: any) => Number(i));
        this.accountingTermForPrimaryOrSecondaryJobEntity =
            data.accountingTermForPrimaryOrSecondaryJobEntity;
    }
    validIds: number[];
    invalidIds: number[];
    accountingTermForPrimaryOrSecondaryJobEntity: string;
}

export interface IBillsMassDeleteRequest {
    selectedIds: IBaseEntity[];
}

export class CopyBillsResponse {
    constructor(data: any) {
        this.exportedBillIds = data.exportedBillIds;
        this.subsNotOnJobsite = data.subsNotOnJobsite.map((sub: any) => new BTSelectItem(sub));
        this.internalUsersNotOnJobsite = data.internalUsersNotOnJobsite.map(
            (user: any) => new BTSelectItem(user),
        );
    }

    exportedBillIds: number[];
    subsNotOnJobsite: BTSelectItem[];
    internalUsersNotOnJobsite: BTSelectItem[];
}

export class ApplyLienWaiverResponse {
    constructor(data: any) {
        this.eligibleBills = data.eligibleBills;
        this.lienWaiverForms = data.lienWaiverForms.map((x: any) => new BTSelectItem(x));
    }

    eligibleBills: IBaseEntity[];
    lienWaiverForms: BTSelectItem[];
}

export class MarkReadyForPaymentResponse {
    constructor(data: any) {
        this.totalPaymentCount = data.totalPaymentCount;
        this.successfulCount = data.successfulCount;
    }
    totalPaymentCount: number;
    successfulCount: number;
}
